import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Modal, Button } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import HeaderLogo from './logo';
import HeaderButton from './button';
// import SearchForm from '../searchForm';
import { partnersList, } from '../footer/helpers';
import { logout } from '../../../../actions';

import { isMobile } from '../../../../utils/mobile';
import FooterNav from '../footer/footerNav';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import { getNotifications, markNotificationsAsRead, markAllNotificationsAsRead } from '../../../../api';
import parseNotificationMessage from '../../../../utils/parseNotificationMessage';
import screeningLogo from '../../../../assets/img/skrinig40.svg';
import screeningLogoSmall from '../../../../assets/img/skrinig40_52x48.svg';
import tryzubLogo from '../../../../assets/img/tryzub.svg';
import nszuLogo from '../../../../assets/img/nszu.svg';
import Auth from '../../../../containers/Auth';

// Generate app version based on current date (can be replaced with process.env.REACT_APP_VERSION)
const APP_VERSION = process.env.REACT_APP_BUILD_DATE || `v${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
const EDNIPRO_POPUP_DEV_DISABLE_KEY = 'ednipro_popup_dev_disabled';

// Version check configuration
const VERSION_CHECK_CONFIG = {
  CHECK_INTERVAL: 5 * 60 * 1000, // 5 minutes
  MAX_RETRIES: 3,
  RETRY_DELAY: 30 * 1000, // 30 seconds
  RELOAD_COOLDOWN: 10 * 60 * 1000, // 10 minutes cooldown between reloads
  STORAGE_KEYS: {
    LAST_RELOAD: 'lastVersionReload',
    RETRY_COUNT: 'versionCheckRetries',
    LAST_CHECK: 'lastVersionCheck'
  }
};

// Function to extract version number from version string
const extractVersionNumber = (versionString) => {
  if (!versionString) return null;
  
  // Remove 'v' prefix and extract numbers
  const cleaned = versionString.replace(/^v/, '').replace(/[^\d]/g, '');
  
  // Validate format (should be YYYYMMDD or YYYYMMDDHHMM)
  if (cleaned.length >= 8 && cleaned.length <= 12) {
    return parseInt(cleaned, 10);
  }
  
  return null;
};

// Function to check if version checking should be performed
const shouldCheckVersion = () => {
  const isProduction = process.env.REACT_APP_ENV?.toLowerCase() === 'production';
  const isStaging = process.env.REACT_APP_ENV?.toLowerCase() !== 'production' && 
                    localStorage.getItem('apiEnv') !== 'prod';
  
  return isProduction || isStaging;
};

// Function to check if enough time has passed since last reload
const canReload = () => {
  const lastReload = localStorage.getItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.LAST_RELOAD);
  if (!lastReload) return true;
  
  const timeSinceLastReload = Date.now() - parseInt(lastReload, 10);
  return timeSinceLastReload > VERSION_CHECK_CONFIG.RELOAD_COOLDOWN;
};

// Function to check if enough time has passed since last check
const shouldPerformCheck = () => {
  const lastCheck = localStorage.getItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.LAST_CHECK);
  if (!lastCheck) return true;
  
  const timeSinceLastCheck = Date.now() - parseInt(lastCheck, 10);
  return timeSinceLastCheck > VERSION_CHECK_CONFIG.CHECK_INTERVAL;
};

// Main version check function
const checkVersionAndReload = async (options = {}) => {
  const {
    performCheck,
    forceCheck = false,
    showUserMessages = false,
    skipCooldowns = false
  } = options;

  // Check if version checking is enabled for current environment
  if (!forceCheck && !shouldCheckVersion()) {
    return { success: false, reason: 'Version checking disabled for this environment' };
  }
  
  // Check if enough time has passed since last check (unless forced)
  if (!forceCheck && performCheck === undefined && !shouldPerformCheck()) {
    return { success: false, reason: 'Check interval not reached' };
  }
  
  // Check if we can reload (cooldown protection, unless skipped)
  if (!skipCooldowns && !canReload()) {
    const message = 'Version reload cooldown active, skipping check';
    console.log(message);
    return { success: false, reason: message };
  }
  
  try {
    // Update last check timestamp (unless forced manual check)
    if (!forceCheck) {
      localStorage.setItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.LAST_CHECK, Date.now().toString());
    }
    
    // Get current retry count (skip for forced checks)
    const retryCount = forceCheck ? 0 : parseInt(localStorage.getItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.RETRY_COUNT) || '0', 10);
    
    // Check if we've exceeded max retries (skip for forced checks)
    if (!forceCheck && retryCount >= VERSION_CHECK_CONFIG.MAX_RETRIES) {
      const message = 'Max version check retries exceeded, waiting for cooldown';
      console.log(message);
      return { success: false, reason: message };
    }
    
    // Fetch version file with cache busting
    const cacheBuster = Date.now();
    const response = await fetch(`/version.txt?v=${cacheBuster}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      timeout: 10000 // 10 second timeout
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const serverVersion = (await response.text()).trim();
    
    // Extract version numbers for comparison
    const currentVersionNum = extractVersionNumber(APP_VERSION);
    const serverVersionNum = extractVersionNumber(serverVersion);
    
    const logMessage = forceCheck ? 'Manual version check:' : 'Version check:';
    // console.log(logMessage, {
    //   current: APP_VERSION,
    //   server: serverVersion,
    //   currentNum: currentVersionNum,
    //   serverNum: serverVersionNum
    // });
    
    // Reset retry count on successful check (only for automatic checks)
    if (!forceCheck) {
      localStorage.removeItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.RETRY_COUNT);
    }
    
    // Check if server version is newer
    if (serverVersionNum && currentVersionNum && serverVersionNum > currentVersionNum) {
      console.log('New version detected, preparing reload...');
      
      // Show user notification
      const confirmMessage = showUserMessages 
        ? `Доступна нова версія: ${serverVersion}. Поточна версія: ${APP_VERSION}. Оновити зараз?`
        : 'Доступна нова версія додатку. Оновити зараз?';
      
      const shouldReload = window.confirm && typeof window.confirm === 'function' 
        ? window.confirm(confirmMessage)
        : true;
      
      if (!shouldReload) {
        return { success: true, hasUpdate: true, userDeclined: true };
      }
      
      // Set reload timestamp to prevent immediate reload loops
      localStorage.setItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.LAST_RELOAD, Date.now().toString());
      
      // Perform reload
      setTimeout(() => {
        try {
          // Try different reload methods
          if (window.location.reload) {
            window.location.reload(true);
          } else {
            window.location.href = window.location.href;
          }
        } catch (error) {
          console.error('Error during reload:', error);
          // Fallback reload method
          window.location.assign(window.location.href);
        }
      }, 1000); // Small delay to ensure localStorage is updated
      
      return { success: true, hasUpdate: true, reloading: true };
      
    } else if (!serverVersionNum || !currentVersionNum) {
      const errorMessage = 'Invalid version format detected';
      console.warn(errorMessage, {
        current: APP_VERSION,
        server: serverVersion
      });
      
      if (showUserMessages) {
        alert('Помилка: Неможливо визначити версію додатку');
      }
      
      return { success: false, reason: errorMessage };
    } else {
      // console.log('Version is up to date');
      
      if (showUserMessages) {
        alert(`Ви використовуєте останню версію: ${APP_VERSION}`);
      }
      
      return { success: true, hasUpdate: false, upToDate: true };
    }
    
  } catch (error) {
    // console.error('Version check failed:', error);
    
    if (showUserMessages) {
      alert('Помилка при перевірці оновлень. Спробуйте пізніше.');
    }
    
    // Increment retry count (only for automatic checks)
    if (!forceCheck) {
      const newRetryCount = parseInt(localStorage.getItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.RETRY_COUNT) || '0', 10) + 1;
      localStorage.setItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.RETRY_COUNT, newRetryCount.toString());
      
      // If we haven't exceeded max retries, schedule a retry
      if (newRetryCount < VERSION_CHECK_CONFIG.MAX_RETRIES) {
        setTimeout(() => {
          checkVersionAndReload();
        }, VERSION_CHECK_CONFIG.RETRY_DELAY);
      } else {
        // console.error('Max version check retries exceeded');
        // Clear retry count after extended delay to allow future checks
        setTimeout(() => {
          localStorage.removeItem(VERSION_CHECK_CONFIG.STORAGE_KEYS.RETRY_COUNT);
        }, VERSION_CHECK_CONFIG.RELOAD_COOLDOWN);
      }
    }
    
    return { success: false, reason: error.message };
  }
};

function HeaderNotifications({
  user,
  showNotifications,
  notifications,
  notificationLoading,
  hasUnreadNotifications,
  unreadCount,
  hasMoreNotifications,
  loadingMore,
  onBellClick,
  onClose,
  onMarkAllRead,
  onMarkOneRead,
  onScroll,
  onNavigate,
  onRefresh,
}) {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const touchStartYRef = useRef(0);
  const isPullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const PULL_THRESHOLD = 70;

  useOutsideClick(wrapperRef, () => {
    if (showNotifications) onClose();
  });

  const handleTouchStart = (e) => {
    const el = contentRef.current;
    if (el && el.scrollTop === 0) {
      touchStartYRef.current = e.touches[0].clientY;
      isPullingRef.current = true;
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPullingRef.current) return;
    const el = contentRef.current;
    if (!el || el.scrollTop > 0) {
      isPullingRef.current = false;
      setIsPulling(false);
      pullDistanceRef.current = 0;
      setPullDistance(0);
      return;
    }
    const distance = e.touches[0].clientY - touchStartYRef.current;
    if (distance > 0) {
      const clamped = Math.min(distance * 0.5, PULL_THRESHOLD + 20);
      pullDistanceRef.current = clamped;
      setPullDistance(clamped);
    }
  };

  const handleTouchEnd = async () => {
    if (!isPullingRef.current) return;
    isPullingRef.current = false;
    setIsPulling(false);
    const currentPullDistance = pullDistanceRef.current;
    pullDistanceRef.current = 0;
    setPullDistance(0);
    if (currentPullDistance >= PULL_THRESHOLD && onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  if (!user?.token) return null;
  return (
    <div className="mobile-notification-wrapper" ref={wrapperRef}>
      <button 
        className={`mobile-notification-btn ${hasUnreadNotifications ? 'has-unread' : ''}`} 
        onClick={onBellClick}
      >
        <i className="far fa-bell" />
        {hasUnreadNotifications && (
          <span className="notification-badge">
            {unreadCount > 0 && unreadCount <= 99 ? unreadCount : unreadCount > 99 ? '99+' : ''}
          </span>
        )}
      </button>

      {showNotifications && (
        <div className="mobile-notifications-fullscreen">
          <div className="mobile-notifications-header">
            <h3>Сповіщення</h3>
            {hasUnreadNotifications && (
              <button className="mobile-notifications-read-all" onClick={onMarkAllRead}>
                <i className="fas fa-check-double" /> Прочитати все
              </button>
            )}
            <button className="mobile-notifications-close" onClick={onClose}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div
            className="mobile-notifications-content"
            ref={contentRef}
            onScroll={onScroll}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {(pullDistance > 0 || isRefreshing) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                overflow: 'hidden',
                height: isRefreshing ? '50px' : `${pullDistance}px`,
                transition: isPulling ? 'none' : 'height 0.3s ease',
                color: '#6c757d',
                fontSize: '14px',
              }}>
                <i className={`fas ${isRefreshing ? 'fa-spinner fa-spin' : pullDistance >= PULL_THRESHOLD ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                <span>{isRefreshing ? 'Оновлення...' : pullDistance >= PULL_THRESHOLD ? 'Відпустіть' : 'Потягніть вниз'}</span>
              </div>
            )}
            {notificationLoading ? (
              <div className="mobile-notifications-loading">
                <i className="fas fa-spinner fa-spin" />
                <p>Завантаження сповіщень...</p>
              </div>
            ) : notifications.length > 0 ? (
              <>
                <div className="mobile-notifications-list">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`mobile-notification-item ${!notification.read_at ? 'unread' : ''}`}
                    >
                      <div className="mobile-notification-text">
                        {parseNotificationMessage(notification.message || notification.body, onNavigate)}
                      </div>
                      <div className="mobile-notification-footer">
                        <div className="mobile-notification-time">
                          {new Date(notification.created_at).toLocaleDateString('uk-UA', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        {!notification.read_at && (
                          <button
                            className="mobile-notification-read-btn"
                            onClick={(e) => { e.stopPropagation(); onMarkOneRead(notification.id); }}
                          >
                            <i className="fas fa-check" /> Прочитати
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {loadingMore && (
                  <div className="mobile-notifications-loading-more">
                    <i className="fas fa-spinner fa-spin" />
                    <span>Завантаження...</span>
                  </div>
                )}
                {!hasMoreNotifications && notifications.length > 0 && (
                  <div className="mobile-notifications-end">
                    <p>Усі сповіщення завантажено</p>
                  </div>
                )}
              </>
            ) : (
              <div className="mobile-notifications-empty">
                <i className="far fa-bell-slash" />
                <p>Немає сповіщень</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Header = ({ city, user, showTitle, searchFilters, logout }) => {
  const history = useHistory();

  // Logout handler
  const onLogout = useCallback(() => {
    history.push('/');
    logout();
  }, [history, logout]);

  // Delegate link clicks inside HTML notification content
  const handleNotifLinkClick = useCallback((e) => {
    const link = e.target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href) return;
    if (href.startsWith('/') || href.startsWith(window.location.origin)) {
      e.preventDefault();
      const path = href.startsWith('/') ? href : href.replace(window.location.origin, '');
      history.push(path);
    }
  }, [history]);

  const [isMobileView, setIsMobileView] = useState(isMobile());
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [versionClickCount, setVersionClickCount] = useState(0);
  const [versionClickTimer, setVersionClickTimer] = useState(null);
  const [showAdvancedMenu, setShowAdvancedMenu] = useState(false);
  const [showNSZUModal, setShowNSZUModal] = useState(false);
  const [isEdniproPopupDisabledForDev, setIsEdniproPopupDisabledForDev] = useState(
    () => localStorage.getItem(EDNIPRO_POPUP_DEV_DISABLE_KEY) === 'true',
  );

  // Refs to store current values for global notification handler
  const userRef = useRef(user);
  const loadInitialNotificationsRef = useRef();
  const handleNotificationClickRef = useRef();
  // Prevents duplicate initial load if token changes twice (e.g. getSettings refresh)
  const notifsFetchedRef = useRef(false);
  const notifsFetchInFlightRef = useRef(false);

  const handleNSZUClick = (e) => {
    setShowNSZUModal(true);
    e.preventDefault();
  };
  
  const handleOpenNSZU = () => {
    setShowNSZUModal(false);
    window.open('https://my.ehealth.gov.ua/', '_blank', 'noopener,noreferrer');
  };

  // Update refs when values change
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handlePathChange = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePathChange);
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  // Function to load initial notifications (can be called externally)
  const loadInitialNotifications = useCallback(async () => {
    if (!user?.token || notifsFetchInFlightRef.current) return;
    notifsFetchInFlightRef.current = true;
    try {
      const response = await getNotifications(1, 10);
      if (response && response.data) {
        setNotifications(response.data);
        setHasMoreNotifications(response.data.length === 10);
        const count = response.unread_count ?? response.data.filter(n => !n.read_at).length;
        setUnreadCount(count);
        setHasUnreadNotifications(count > 0);
        window.dispatchEvent(new CustomEvent('headerNotificationsLoaded', {
          detail: { notifications: response.data },
        }));
      }
    } catch (error) {
      console.error('Error loading initial notifications:', error);
    } finally {
      notifsFetchInFlightRef.current = false;
    }
  }, [user?.token]);

  // Keep ref always current for onNotificationReceived
  useEffect(() => {
    loadInitialNotificationsRef.current = loadInitialNotifications;
  }, [loadInitialNotifications]);

  // One-time initial load — fires when token first becomes available
  useEffect(() => {
    if (!user?.token || notifsFetchedRef.current) return;
    notifsFetchedRef.current = true;
    loadInitialNotifications();
  }, [user?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleBannerRead = (e) => {
      const { id, remainingUnread } = e.detail;
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n)),
      );
      setUnreadCount(remainingUnread);
      setHasUnreadNotifications(remainingUnread > 0);
    };
    window.addEventListener('bannerNotificationRead', handleBannerRead);
    return () => window.removeEventListener('bannerNotificationRead', handleBannerRead);
  }, []);

  // Initialize version checking
  useEffect(() => {
    if(isMobileView) {
      // Perform initial version check after component mount
      const initialCheckTimer = setTimeout(() => {
        checkVersionAndReload();
      }, 2000); // Wait 2 seconds after app load

      // Set up periodic version checking
      const versionCheckInterval = setInterval(() => {
        checkVersionAndReload();
      }, VERSION_CHECK_CONFIG.CHECK_INTERVAL);

      // Cleanup timers on unmount
      return () => {
        clearTimeout(initialCheckTimer);
        clearInterval(versionCheckInterval);
      };
    }
  }, []);

  // Check version when app becomes visible (user returns to tab/app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // App became visible, check version
        setTimeout(() => {
          checkVersionAndReload();
        }, 2000); // Small delay to ensure app is fully active
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Cleanup version click timer on unmount
  useEffect(() => {
    return () => {
      if (versionClickTimer) {
        clearTimeout(versionClickTimer);
      }
    };
  }, [versionClickTimer]);

  const handleBackClick = () => {
    window.history.back();
  };

  const handleMenuToggle = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const handleNotificationClick = useCallback(async () => {
    if (!user?.token) return;

    if (showNotifications) {
      setShowNotifications(false);
      return;
    }

    setShowNotifications(true);

    // Already have data or initial load is in flight — no second request needed
    if (notifications.length > 0 || notifsFetchInFlightRef.current) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'NOTIFICATIONS_READ' }));
      }
      return;
    }

    setCurrentPage(1);
    setHasMoreNotifications(true);
    setNotificationLoading(true);
    try {
      const response = await getNotifications(1, 10);
      if (response && response.data) {
        setNotifications(response.data);
        setHasMoreNotifications(response.data.length === 10);
        const count = response.unread_count ?? response.data.filter(n => !n.read_at).length;
        setUnreadCount(count);
        setHasUnreadNotifications(count > 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setNotificationLoading(false);
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'NOTIFICATIONS_READ' }));
      }
    }
  }, [user?.token, showNotifications, notifications.length]);

  // Update ref when function changes
  useEffect(() => {
    handleNotificationClickRef.current = handleNotificationClick;
  }, [handleNotificationClick]);

  // Setup global notification handler (after handleNotificationClick is defined)
  useEffect(() => {
    window.onNotificationReceived = function(notification, isClicked) {
      // Always reload notifications when a new one is received
      if (loadInitialNotificationsRef.current) {
        loadInitialNotificationsRef.current();
      }
      
      // If notification was clicked, show the notifications popup
      if (isClicked && userRef.current?.token && handleNotificationClickRef.current) {
        handleNotificationClickRef.current();
      }
    };

    // Cleanup function to remove global handler
    return () => {
      window.onNotificationReceived = undefined;
    };
  }, []); // Remove dependencies since we're using refs

  const handleNotificationItemClick = (notification) => {};

  const handleMarkOneRead = useCallback(async (id) => {
    try {
      await markNotificationsAsRead(id);
      setNotifications((prev) => {
        const updated = prev.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        );
        const newCount = updated.filter((n) => !n.read_at).length;
        setUnreadCount(newCount);
        setHasUnreadNotifications(newCount > 0);
        return updated;
      });
      window.dispatchEvent(new CustomEvent('notificationRead', { detail: { id } }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })),
      );
      setUnreadCount(0);
      setHasUnreadNotifications(false);
      window.dispatchEvent(new CustomEvent('notificationsAllRead'));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const loadMoreNotifications = async () => {
    if (!user?.token || !hasMoreNotifications || loadingMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await getNotifications(nextPage, 10);
      if (response && response.data && response.data.length > 0) {
        setNotifications(prev => [...prev, ...response.data]);
        setCurrentPage(nextPage);
        setHasMoreNotifications(response.data.length === 10);
      } else {
        setHasMoreNotifications(false);
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // Load when 100px from bottom
    
    if (isNearBottom && hasMoreNotifications && !loadingMore) {
      loadMoreNotifications();
    }
  };

  const handleRefreshNotifications = useCallback(async () => {
    if (notifsFetchInFlightRef.current) return;
    notifsFetchInFlightRef.current = true;
    try {
      const response = await getNotifications(1, 10);
      if (response && response.data) {
        setNotifications(response.data);
        setCurrentPage(1);
        setHasMoreNotifications(response.data.length === 10);
        const count = response.unread_count ?? response.data.filter(n => !n.read_at).length;
        setUnreadCount(count);
        setHasUnreadNotifications(count > 0);
      }
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      notifsFetchInFlightRef.current = false;
    }
  }, []);

  const handleVersionClick = () => {
    const newCount = versionClickCount + 1;
    setVersionClickCount(newCount);

    // Clear existing timer
    if (versionClickTimer) {
      clearTimeout(versionClickTimer);
    }
    
    // If this is the third click within 2 seconds, show advanced menu
    if (newCount === 3) {
      setShowAdvancedMenu(!showAdvancedMenu);
      setVersionClickCount(0); // Reset counter
      return;
    }

    // Set timer to reset counter after 2 seconds
    const timer = setTimeout(() => {
      setVersionClickCount(0);
      setVersionClickTimer(null);
    }, 1000);

    setVersionClickTimer(timer);
  };

  const handleEnvironmentSwitch = () => {
    const isProduction = process.env.REACT_APP_ENV?.toLowerCase() === 'production';
    const targetUrl = isProduction ? 'https://staging.likari.in.ua/' : 'https://likari.in.ua/';
    window.location.href = targetUrl;
  };

  const handleDebugMode = () => {
    if (window.activateDebugMode && typeof window.activateDebugMode === 'function') {
      window.activateDebugMode();
    } else {
      console.log('Debug mode function not available');
      alert('Функція відладки недоступна');
    }
  };

  const handleCheckForUpdates = async () => {
    await checkVersionAndReload({
      forceCheck: true,
      showUserMessages: true,
      skipCooldowns: true
    });
  };

  const handleToggleEdniproPopup = () => {
    const nextValue = !isEdniproPopupDisabledForDev;
    setIsEdniproPopupDisabledForDev(nextValue);
    localStorage.setItem(EDNIPRO_POPUP_DEV_DISABLE_KEY, nextValue ? 'true' : 'false');
    window.dispatchEvent(new CustomEvent('edniproPopupDevToggle', { detail: { disabled: nextValue } }));
  };
  
  const handleRefreshWindow = () => {
    const currentUrl = window.location.href;
    const userUrl = prompt('Введіть URL для переходу:', currentUrl);
    
    if (userUrl !== null) { // User didn't cancel
      try {
        // Validate URL format
        new URL(userUrl);
        window.location.href = userUrl;
      } catch (error) {
        alert('Неправильний формат URL. Спробуйте ще раз.');
      }
    }
  };

  // Check if current page is home page
  const isHomePage = currentPath === '/';

  // NSZU Modal Component (used in both mobile and desktop)
  const NSZUModal = (
    <Modal show={showNSZUModal} onHide={() => setShowNSZUModal(false)} centered size="lg" className="nszu-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <img src={nszuLogo} alt="НСЗУ" style={{ height: '32px', marginRight: '12px', verticalAlign: 'middle' }} />
            Державний кабінет пацієнта
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="lead">Безкоштовний електронний сервіс для громадян України.</p>
          
          <h6 className="mt-4 mb-3">Що можна зробити в кабінеті:</h6>
          <ul className="nszu-features-list">
            <li>
              <i className="fas fa-file-medical text-primary"></i>
              <span>Перегляд чинної декларації з лікарем первинної медичної допомоги, а також перегляд декларацій, поданих раніше</span>
            </li>
            <li>
              <i className="fas fa-user-md text-primary"></i>
              <span>Зручний пошук лікаря та подання декларації онлайн</span>
            </li>
            <li>
              <i className="fas fa-user-edit text-primary"></i>
              <span>Оновлення персональних даних</span>
            </li>
            <li>
              <i className="fas fa-user-shield text-primary"></i>
              <span>Управління доступами, які були раніше надані пацієнтським інформаційним системам: перегляд та відкликання</span>
            </li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleOpenNSZU}>
            <i className="fas fa-external-link-alt mr-2"></i>
            Відкрити кабінет
          </Button>
        </Modal.Footer>
      </Modal>
  );

  const isApp = !!window.ReactNativeWebView;
  const hide_footer = isApp ? user?.edniproPopup?.app_hide_footer : user?.edniproPopup?.web_hide_footer;

  // Mobile header
  if (isMobileView) {
    return (
      <>
        <header className="header-mobile">
          <div className="mobile-header-content">
            {/* Back button - only show if not on home page */}
            {!isHomePage && (
              <button className="mobile-back-btn" onClick={handleBackClick}>
                <i className="fas fa-chevron-left" />
              </button>
            )}
            
            <div className={`mobile-logos ${isHomePage ? 'mobile-logos--centered' : ''}`}>
              <HeaderLogo city={city} />
              <a href={partnersList[0].link} className="mobile-partner-link" target='_blank' rel="noopener noreferrer">
                <img src={partnersList[0].item} alt={partnersList[0].alt} className="mobile-partner-logo"/>
              </a>
              <a href="#" onClick={handleNSZUClick} className="login-btn nszu-link" rel="noopener noreferrer" title="Державний кабінет пацієнта">
                <div className="login-btn__description">
                  <img src={nszuLogo} alt="Державний кабінет пацієнта" style={{ maxHeight: '24px' }}/>                  
                </div>
              </a>
            </div>
            
            <div className="mobile-header-actions">
              <HeaderNotifications
                user={user}
                showNotifications={showNotifications}
                notifications={notifications}
                notificationLoading={notificationLoading}
                hasUnreadNotifications={hasUnreadNotifications}
                unreadCount={unreadCount}
                hasMoreNotifications={hasMoreNotifications}
                loadingMore={loadingMore}
                onBellClick={handleNotificationClick}
                onClose={() => setShowNotifications(false)}
                onMarkAllRead={handleMarkAllRead}
                onMarkOneRead={handleMarkOneRead}
                onScroll={handleScroll}
                onNavigate={history.push}
                onRefresh={handleRefreshNotifications}
              />
              <button className="mobile-menu-btn" onClick={handleMenuToggle}>
                <i className="fas fa-bars" />
              </button>
            </div>
          </div>
        </header>
        
        {/* Mobile Menu Modal */}
        {showMobileMenu && (
          <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
            <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-menu-header">
                <h3>Меню</h3>
                <button className="mobile-menu-close" onClick={() => setShowMobileMenu(false)}>
                  ×
                </button>
              </div>
              <div className="mobile-menu-body">
                <div className="footer-navigation">
                  <ul className="about-project-list" style={{ marginBottom: '10px' }}>
                    <li onClick={handleNSZUClick} className="about-project-list__item">
                    <img src={nszuLogo} alt="Державний кабінет пацієнта" style={{ maxHeight: '14px', marginRight: '8px' }}/>
                    <a className="about-project-list__link" href="#">Державний кабінет пацієнта</a></li>
                  </ul>
                </div>
                <FooterNav />
                {hide_footer && user?.token && user?.city?.id === 1 && (
                  <button onClick={onLogout} className="mobile-menu-logout-btn">
                    <i className="fas fa-sign-out-alt" />
                    Вихід
                  </button>
                )}
                <div className="mobile-app-version">
                  <div className="version-info" onClick={handleVersionClick}>
                    Версія: {APP_VERSION}
                  </div>
                  
                  {showAdvancedMenu && (
                    <div className="advanced-menu">
                      <div className="advanced-menu-item">
                        <span>DeviceId: {window.deviceId ? window.deviceId : 'unknown'}</span>
                      </div>
                      <div className="advanced-menu-item clickable" onClick={handleCheckForUpdates}>
                        <i className="fas fa-sync-alt"></i>
                        <span>Перевірити оновлення</span>
                      </div>
                      <div className="advanced-menu-item clickable" onClick={handleRefreshWindow}>
                        <i className="fas fa-redo"></i>
                        <span>Оновити вікно</span>
                      </div>
                      <div className="advanced-menu-item clickable" onClick={handleDebugMode}>
                        <i className="fas fa-bug"></i>
                        <span>Відкрити лог відладки</span>
                      </div>
                      <div className="advanced-menu-item clickable" onClick={handleToggleEdniproPopup}>
                        <i className={`fas ${isEdniproPopupDisabledForDev ? 'fa-toggle-off' : 'fa-toggle-on'}`}></i>
                        <span>
                          {isEdniproPopupDisabledForDev ? 'Увімкнути eDnipro popup' : 'Вимкнути eDnipro popup'}
                        </span>
                      </div>
                      <div className="advanced-menu-item clickable" onClick={handleEnvironmentSwitch}>
                        <i className="fas fa-exchange-alt"></i>
                        <span>
                          Змінити середовище на {process.env.REACT_APP_ENV?.toLowerCase() === 'production' ? 'DEMO' : 'PROD'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* NSZU Modal for mobile */}
        {NSZUModal}

        {/* Global force-auth gate */}
        <Auth isOpen={false} onClose={() => {}} />
        
        <style>{`
          .nszu-features-list {
            list-style: none;
            padding: 0;
          }
          .nszu-features-list li {
            display: flex;
            align-items: flex-start;
            margin-bottom: 12px;
            padding: 8px;
            border-radius: 4px;
            transition: background-color 0.2s;
          }
          .nszu-features-list li:hover {
            background-color: #f8f9fa;
          }
          .nszu-features-list i {
            margin-right: 12px;
            margin-top: 2px;
            font-size: 18px;
            min-width: 20px;
          }
          .nszu-features-list span {
            flex: 1;
            line-height: 1.5;
          }
          .nszu-modal .modal-content {
            border-radius: 12px;
            padding: 10px;
          }
          .nszu-modal .modal-title {
            width: 90%;
            font-size: 20px;
          }
          .mobile-menu-logout-btn {
            display: flex;
            align-items: center;
            gap: 10px;
            width: calc(100% - 32px);
            margin: 12px 16px 4px;
            padding: 13px 18px;
            background: #fff5f5;
            color: #d32f2f;
            border: 1.5px solid #ffcdd2;
            border-radius: 10px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.18s, border-color 0.18s;
          }
          .mobile-menu-logout-btn:active {
            background: #ffebee;
            border-color: #ef9a9a;
          }
          .mobile-menu-logout-btn i {
            font-size: 16px;
          }
        `}</style>
      </>
    );
  }

  // Desktop header
  return (
    <>
    <header className="header header--search header--feature">
      <Container>
        <HeaderLogo city={city} />

        {/* <SearchForm /> */}
        {showTitle ? (
          searchFilters?.screening_40_plus ? (
            <Link to="/national-screening" className="title-online-visit screening-title-link">
              <img 
                src={tryzubLogo} 
                alt="Тризуб" 
                className="tryzub-logo-header"
              />
              <img 
                src={screeningLogo} 
                alt="Скринінг здоров'я 40+" 
                className="screening-logo-header"
              />
            </Link>
          ) : (
            <h3 className="title-online-visit">Онлайн-запис на прийом до лікаря</h3>
          )
        ) : (
          <span className="span-flex"></span>
        )}

        <a href={partnersList[0].link} className="partners-list__link" target='_blank' rel="noopener noreferrer" >
            <img src={partnersList[0].item} alt={partnersList[0].alt}/>
        </a>
        
        <a href="#" onClick={handleNSZUClick} className="login-btn nszu-link" rel="noopener noreferrer" title="Державний кабінет пацієнта">
            <div className="login-btn__description">
              <img src={nszuLogo} alt="Державний кабінет пацієнта" style={{ maxHeight: '24px', marginRight: '8px' }}/>
              <span className="nszu-text">Державний кабінет<br/> пацієнта ЕСОЗ</span>
            </div>
        </a>
        
        <HeaderNotifications
          user={user}
          showNotifications={showNotifications}
          notifications={notifications}
          notificationLoading={notificationLoading}
          hasUnreadNotifications={hasUnreadNotifications}
          unreadCount={unreadCount}
          hasMoreNotifications={hasMoreNotifications}
          loadingMore={loadingMore}
          onBellClick={handleNotificationClick}
          onClose={() => setShowNotifications(false)}
          onMarkAllRead={handleMarkAllRead}
          onMarkOneRead={handleMarkOneRead}
          onScroll={handleScroll}
          onNavigate={history.push}
          onRefresh={handleRefreshNotifications}
        />
        <HeaderButton />
      </Container>
      <style>{`
        .screening-title-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          text-decoration: none;
          transition: all 0.3s ease;
        }
        
        .screening-title-link:hover {
          text-decoration: none;
          transform: translateY(-2px);
        }
        
        .tryzub-logo-header {
          height: 50px;
          width: auto;
        }
        
        .screening-logo-header {
          height: 50px;
          width: auto;
        }
        .nszu-features-list {
          list-style: none;
          padding: 0;
        }
        .nszu-features-list li {
          display: flex;
          align-items: flex-start;
          margin-bottom: 12px;
          padding: 8px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }
        .nszu-features-list li:hover {
          background-color: #f8f9fa;
        }
        .nszu-features-list i {
          margin-right: 12px;
          margin-top: 2px;
          font-size: 18px;
          min-width: 20px;
        }
        .nszu-features-list span {
          flex: 1;
          line-height: 1.5;
        }
      `}</style>
    </header>
    
    {/* NSZU Modal for desktop */}
    {NSZUModal}

    {/* Global force-auth gate */}
    <Auth isOpen={false} onClose={() => {}} />
    </>
  );
};

const mapDispatchToProps = { logout };

export default connect(null, mapDispatchToProps)(Header);
